import { ChangeEvent, DragEvent, useEffect, useState } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import { FC, useRef } from "react";
import { Upload } from "lucide-react";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";
import VideoUploadProgress from "src/components/AssetUploaders/VideoUploadProgress";
import VideoProcessingProgress from "src/components/AssetUploaders/VideoProcessingProgress";
import { useVideoEncodingProgress } from "src/components/AssetUploaders/useVideoEncodingProgress";
import type { ChunkedUploadState } from "src/components/AssetUploaders/useAzureFileUpload";

interface Props {
	stepNumber: number;
	placeholder: string;
	control: any;
	error: boolean;
	upload: (file: File, uploadType: string) => void;
	uuid: string | null;
	done: boolean;
	uploading: boolean;
	setValue: UseFormSetValue<BannerFormState>;
	videoUploadState?: ChunkedUploadState | null;
	onCancelUpload?: () => void;
}

const VideoUpload: FC<Props> = ({
	stepNumber,
	placeholder,
	control,
	upload,
	uuid,
	uploading,
	setValue,
	videoUploadState,
	onCancelUpload
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	// Local blob URL for the just-selected file. Same pattern as the
	// announcement editor and FileUploadStep — show the user's video
	// playing immediately while upload + Bitmovin encoding happen in
	// the background, with the dark overlay for progress on top.
	const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

	const bannerType = useWatch({ name: "bannerType", control });

	const value = useWatch({ name: `steps.${stepNumber - 1}.value`, control });

	const isVideoRequired =
		bannerType === "videoBanner" || bannerType === "promotionVideo";

	const {
		field: { onChange: uuidOnChange, value: uuidValue, ...inputProps }
	} = useController({
		name: `steps.${stepNumber - 1}.value`,
		control,
		rules: { required: isVideoRequired }
	});

	const {
		field: { onChange: fileNameOnChange }
	} = useController({
		name: `steps.${stepNumber - 1}.file`,
		control
	});

	const videoUuid = (uuid || value) as string | undefined;
	const {
		url: videoUrl,
		thumbnail,
		processingStatus
	} = useAzureVideoDownload("video", "", videoUuid || null);
	const encodingState = useVideoEncodingProgress(
		videoUuid || null,
		Boolean(videoUuid) && !videoUrl && processingStatus !== "ReadyForPlayback"
	);

	// Revoke local blob URLs to avoid memory leaks. Cleanup runs both
	// on unmount and whenever the URL is replaced (new file picked).
	useEffect(() => {
		const url = localPreviewUrl;
		if (!url) return;
		return () => URL.revokeObjectURL(url);
	}, [localPreviewUrl]);

	const processFile = (file: File) => {
		const reader = new FileReader();

		reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
			if (!progressEvent?.target) return;

			setValue(
				`steps.${stepNumber - 1}.preview`,
				progressEvent.target.result as string
			);

			setLocalPreviewUrl(URL.createObjectURL(file));
			upload(file, "video");
		};

		reader.readAsDataURL(file);
		fileNameOnChange(file.name);
	};

	const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		if (!target?.files) return;
		processFile(target.files[0]);
	};

	// Wrap the parent-supplied cancel so the local preview is dropped
	// alongside the chunked upload. Without this, an aborted upload
	// would leave a "selected" preview backed by no real asset.
	const handleCancelUpload = () => {
		onCancelUpload?.();
		setLocalPreviewUrl(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (!file) return;

		const isValid =
			file.type === "video/mp4" || file.type === "video/quicktime";
		if (!isValid) {
			alert("Only MP4 / MOV file types can be selected");
			return;
		}

		processFile(file);
	};

	useEffect(() => {
		if (!uuid) return;

		setValue(`steps.${stepNumber - 1}.value`, uuid);
		uuidOnChange(uuid);
	}, [uuid]);

	const hasVideo = !!(uuid || value);
	const isProcessing = processingStatus === "Processing";
	// Show the video frame during upload, processing, and after the
	// streaming URL is available — same UX as the announcement editor
	// and FileUploadStep so every admin video upload site looks and
	// behaves identically.
	const showVideoFrame = uploading || hasVideo || !!localPreviewUrl;
	// While a fresh upload is in flight, prefer the local blob —
	// otherwise a "Change video" on a saved entity would keep showing
	// the OLD streaming URL until the new upload finishes (because
	// `value` still points at the previous asset).
	const videoFrameSrc =
		localPreviewUrl && (uploading || !videoUrl)
			? localPreviewUrl
			: (videoUrl ?? localPreviewUrl ?? undefined);

	return (
		<div className="max-w-[480px]">
			<input
				type="file"
				onChange={onFileSelected}
				accept="video/mp4,video/quicktime"
				{...inputProps}
				name={`steps.${stepNumber - 1}`}
				ref={inputRef}
				style={{ display: "none" }}
				aria-label="file-upload"
			/>

			{showVideoFrame ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle relative">
						{videoFrameSrc ? (
							<video
								src={videoFrameSrc}
								poster={thumbnail || undefined}
								controls
								className="w-full object-contain"
								style={{ aspectRatio: "16/9" }}
								preload="metadata"
							/>
						) : thumbnail ? (
							<div
								className="w-full bg-no-repeat bg-cover bg-center"
								style={{
									backgroundImage: `url(${thumbnail})`,
									aspectRatio: "16/9"
								}}
							/>
						) : (
							<div
								className="w-full bg-foreground"
								style={{ aspectRatio: "16/9" }}
							/>
						)}
						{/* Identical overlay variants to every other admin
						    video upload site (announcement editor, FileUploadStep). */}
						<VideoUploadProgress
							state={uploading ? (videoUploadState ?? null) : null}
							onCancel={handleCancelUpload}
							variant="overlay"
						/>
						{!uploading && !videoUrl && isProcessing && (
							<VideoProcessingProgress
								state={encodingState}
								variant="overlay"
							/>
						)}
					</div>
					{!uploading && (
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							onClick={() => inputRef?.current?.click()}
						>
							Change video
						</button>
					)}
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => inputRef?.current?.click()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							inputRef?.current?.click();
						}
					}}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary-lighter"
							: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
					].join(" ")}
				>
					<Upload className="h-6 w-6 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium text-foreground">
							Drag video here or click to browse
						</p>
						<p className="text-xs text-muted-foreground mt-1">MP4 or MOV</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default VideoUpload;
